import { PaxCostBand, SlidingScalePricingItem, TourService } from '~/types/tours/costPrice';

import { ScaleAnnotation, configurationLabelKey, matchesAnnotationSeason } from './departureInventory';

/**
 * One sliding scale row: a single season of a single package option, which is
 * both the grain of the supplier's CSV and of svc-tour's upload payload.
 */
export interface SlidingScaleRow {
  key: string;
  season: string;
  tourConfiguration: string;
  /** Package and option as one label, e.g. "Base-Regular". */
  configurationLabel: string;
  currency: string;
  singleSupplement: number;
  bands: Array<PaxCostBand>;
}

/** svc-tour stores the scale positionally: index 0 is the rate for 1 pax. */
export function scaleToBands(scale: Array<number>): Array<PaxCostBand> {
  return scale.map((unitPrice, index) => ({ pax: index + 1, unitPrice }));
}

export function buildSlidingScaleRows(items: Array<SlidingScalePricingItem>): Array<SlidingScaleRow> {
  return items.map((item, index) => ({
    key: `${item.tourConfiguration}-${item.packageConfiguration}-${item.season}-${item.currency}-${index}`,
    season: item.season,
    tourConfiguration: item.tourConfiguration,
    configurationLabel: `${item.tourConfiguration}-${item.packageConfiguration}`,
    currency: item.currency,
    singleSupplement: item.singleSupplement,
    bands: scaleToBands(item.scale),
  }));
}

/** Every pax point across all rows, ascending, for building table columns. */
export function getPaxPoints(rows: Array<SlidingScaleRow>): Array<number> {
  const points = new Set<number>();
  rows.forEach((row) => row.bands.forEach((band) => points.add(band.pax)));
  return Array.from(points).sort((a, b) => a - b);
}

/**
 * RM's hedge by tour configuration name: the pax count its sell and cost prices
 * were based on, set in Salesforce as Sliding Pax Pricing. Suppliers share
 * configuration names, so the highest wins, as svc-tour does across deal options.
 */
export function hedgePaxByConfiguration(services: Array<Pick<TourService, 'configurations'>>): Record<string, number> {
  const hedges: Record<string, number> = {};
  services.forEach((service) =>
    service.configurations.forEach(({ name, hedgePax }) => {
      if (typeof hedgePax === 'number') {
        hedges[name] = Math.max(hedges[name] ?? 0, hedgePax);
      }
    }),
  );
  return hedges;
}

/** Travellers a row is bought for on the annotated departure, add-ons stacked on it included. */
export function paxOnRow(row: Pick<SlidingScaleRow, 'configurationLabel'>, annotation: ScaleAnnotation): number {
  const key = configurationLabelKey(row.configurationLabel);
  return (annotation.soldByConfiguration[key] ?? 0) + (annotation.upgradesByConfiguration[key]?.units ?? 0);
}

export interface HedgeStatus {
  tourConfiguration: string;
  hedgePax: number;
  paxSold: number;
}

/**
 * Each hedged tour configuration priced for the departure's season. The hedge is per tour
 * configuration, and its busiest row counts everyone on it, since add-ons stack on the base.
 */
export function hedgeStatuses(
  rows: Array<SlidingScaleRow>,
  annotation: ScaleAnnotation,
  hedges: Record<string, number>,
): Array<HedgeStatus> {
  const statuses = new Map<string, HedgeStatus>();

  rows
    .filter(
      (row) => hedges[row.tourConfiguration] !== undefined && matchesAnnotationSeason(row.season, annotation.season),
    )
    .forEach((row) => {
      const current = statuses.get(row.tourConfiguration);
      const paxSold = Math.max(current?.paxSold ?? 0, paxOnRow(row, annotation));

      statuses.set(row.tourConfiguration, {
        tourConfiguration: row.tourConfiguration,
        hedgePax: hedges[row.tourConfiguration],
        paxSold,
      });
    });

  return Array.from(statuses.values());
}
